import React, { useState, useRef } from "react";
import { Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";

/**
 * Two-phase upload → preview → confirm restore. The user picks a .json.gz
 * they've downloaded previously (from this env or another environment),
 * we round-trip it through /admin/backups/upload with dry_run=True first
 * so they can eyeball what's about to get wiped, then confirm and apply.
 */
export default function RestoreFromFileButton({ onDone }) {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);
  const fileRef = useRef(null);

  const doDryRun = async (file) => {
    setBusy(true);
    setPendingFile(file);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("dry_run", "true");
      const r = await api.post("/admin/backups/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(r.data);
    } catch (err) {
      toast.error("Couldn't read backup", { description: formatApiError(err) });
      setPendingFile(null);
    } finally {
      setBusy(false);
    }
  };

  const doApply = async () => {
    if (!pendingFile) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", pendingFile);
      form.append("dry_run", "false");
      const r = await api.post("/admin/backups/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const total = Object.values(r.data?.loaded || {}).reduce((a, b) => a + b, 0);
      toast.success(`Restored — ${total} rows loaded`);
      setPreview(null);
      setPendingFile(null);
      onDone?.();
    } catch (err) {
      toast.error("Restore failed", { description: formatApiError(err) });
    } finally {
      setBusy(false);
    }
  };

  const cancel = () => {
    setPreview(null);
    setPendingFile(null);
  };

  return (
    <>
      <Button
        variant="outline"
        onClick={() => fileRef.current?.click()}
        disabled={busy}
        data-testid="backup-restore-from-file-btn"
      >
        <Upload className="h-4 w-4 mr-2" />
        Restore from file
      </Button>
      <input
        ref={fileRef}
        type="file"
        accept=".gz,application/gzip"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = "";
          if (f) doDryRun(f);
        }}
      />
      <Dialog open={!!preview} onOpenChange={(o) => !o && cancel()}>
        <DialogContent className="max-w-lg" data-testid="restore-preview-dialog">
          <DialogHeader>
            <DialogTitle>Restore from uploaded file</DialogTitle>
            <DialogDescription>
              This will WIPE every collection listed below and replace it with the
              rows from your uploaded snapshot. This action can't be undone —
              make sure you have a fresh backup first.
            </DialogDescription>
          </DialogHeader>
          {preview && (
            <div className="space-y-3">
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs font-mono">
                <div>source: {preview.source_manifest?.environment_url || "(unknown env)"}</div>
                <div>taken: {preview.source_manifest?.created_at || "?"}</div>
                <div>app version: {preview.source_manifest?.app_version || "?"}</div>
                <div>schema: v{preview.source_manifest?.schema_version || "?"}</div>
              </div>
              <div className="text-sm text-slate-700">
                Will restore <strong className="tabular-nums">{preview.total_incoming_rows.toLocaleString()}</strong> rows across{" "}
                <strong>{Object.keys(preview.incoming_row_counts).length}</strong> collections:
              </div>
              <ul className="text-xs font-mono text-slate-600 space-y-0.5 max-h-48 overflow-auto rounded border border-slate-200 p-2">
                {Object.entries(preview.incoming_row_counts).map(([k, v]) => (
                  <li key={k} className="flex justify-between">
                    <span>{k}</span>
                    <span className="tabular-nums">{v.toLocaleString()}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={cancel} disabled={busy}>
              Cancel
            </Button>
            <Button
              onClick={doApply}
              disabled={busy}
              className="bg-rose-600 text-white hover:bg-rose-700"
              data-testid="restore-preview-confirm"
            >
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Wipe and restore
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
